"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { RfiImage } from "./RfiImageTools";
import { rfiNo } from "../lib/takeoff/rfiReport";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

// ← adjust to the route that opens the takeoff viewer
const takeoffRoute = (takeoffId) => `/takeoff/${takeoffId}`;

const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "X-Tenant-ID": localStorage.getItem("tenantId"),
});

const OPEN_STATUSES = ["draft", "sent"];
const DONE_STATUSES = ["answered", "closed"];
const statusStyle = {
  draft: "bg-gray-500/20 text-gray-300",
  sent: "bg-blue-600/20 text-blue-400",
  answered: "bg-green-600/20 text-green-400",
  closed: "bg-gray-500/20 text-gray-400",
};
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
const isOverdue = (r) =>
  r.status === "sent" &&
  r.dueDate &&
  new Date(r.dueDate) < new Date(new Date().toDateString());
const pictureCount = (r) =>
  (r.items || []).reduce((n, i) => n + (i.images?.length || 0), 0);

const th =
  "px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider";
const metaLabel = "text-xs uppercase tracking-wider text-gray-400";

export default function RFIsTab({ project }) {
  const navigate = useNavigate();
  const [rfis, setRfis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("open");
  const [selectedId, setSelectedId] = useState(null);
  const [bigImage, setBigImage] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const ask = (params) =>
          axios
            .get(`${API_BASE}/rfis`, { headers: headers(), params })
            .then((r) => r.data.rfis || []);

        // the project's RFIs, plus any made while it was still an opportunity
        const lists = [await ask({ projectId: project._id })];
        const oppId = project.opportunityId?._id || project.opportunityId;
        if (oppId) {
          try {
            lists.push(await ask({ opportunityId: oppId }));
          } catch {
            /* opportunity RFIs are optional */
          }
        }

        const seen = new Set();
        const merged = lists
          .flat()
          .filter(
            (r) => r.status !== "void" && !seen.has(r._id) && seen.add(r._id),
          )
          .sort((a, b) => (b.number || 0) - (a.number || 0));

        setRfis(merged);
      } catch (err) {
        setError(
          "Failed to load RFIs: " +
            (err.response?.data?.message || err.message),
        );
      } finally {
        setLoading(false);
      }
    };
    if (project?._id) load();
  }, [project?._id]);

  const filtered = rfis.filter((r) =>
    activeTab === "open"
      ? OPEN_STATUSES.includes(r.status)
      : DONE_STATUSES.includes(r.status),
  );

  if (loading)
    return (
      <div className="text-center py-4 text-gray-200 bg-gray-700">
        Loading RFIs...
      </div>
    );
  if (error)
    return (
      <div className="text-red-400 text-center py-4 bg-gray-700">{error}</div>
    );

  // ───────────────────────── Detail (read-only) ─────────────────────────
  const r = selectedId ? rfis.find((x) => x._id === selectedId) : null;

  if (r) {
    const firstPin = (r.pins || []).find((p) => p.takeoffId);
    return (
      <div className="bg-gray-700 p-6">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
          <h2 className="text-lg font-semibold text-orange-400">
            {rfiNo(r)}: {r.title || "Untitled"}
          </h2>
          <div className="flex gap-2">
            {firstPin && (
              <button
                onClick={() => navigate(takeoffRoute(firstPin.takeoffId))}
                className="px-4 py-2 bg-orange-400 text-gray-200 rounded-md hover:bg-orange-500 transition duration-200"
              >
                Open in Takeoff
              </button>
            )}
            <button
              onClick={() => setSelectedId(null)}
              className="px-4 py-2 bg-gray-600 text-gray-200 rounded-md hover:bg-gray-500 transition duration-200"
            >
              Back to List
            </button>
          </div>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-4 text-sm rounded-md bg-gray-600 p-4 border border-gray-500">
          <div>
            <div className={metaLabel}>Status</div>
            <span
              className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                statusStyle[r.status] || statusStyle.draft
              }`}
            >
              {cap(r.status)}
            </span>
            {isOverdue(r) && (
              <span className="ml-2 rounded bg-red-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-red-300">
                Overdue
              </span>
            )}
          </div>
          <div>
            <div className={metaLabel}>To</div>
            <div className="text-gray-200">
              {[r.to?.name, r.to?.company].filter(Boolean).join(" · ") || "N/A"}
            </div>
            {r.to?.email && (
              <div className="text-xs text-gray-400">{r.to.email}</div>
            )}
            {r.cc?.length > 0 && (
              <div className="text-xs text-gray-400">CC: {r.cc.join(", ")}</div>
            )}
          </div>
          <div>
            <div className={metaLabel}>Answer needed by</div>
            <div className="text-gray-200">
              {r.dueDate ? new Date(r.dueDate).toLocaleDateString() : "N/A"}
            </div>
          </div>
          <div>
            <div className={metaLabel}>Priority</div>
            <div className="text-gray-200">{cap(r.priority || "normal")}</div>
          </div>
          <div>
            <div className={metaLabel}>Cost impact</div>
            <div className="text-gray-200">
              {cap(r.costImpact || "unknown")}
            </div>
          </div>
          <div>
            <div className={metaLabel}>Schedule impact</div>
            <div className="text-gray-200">
              {cap(r.scheduleImpact || "unknown")}
            </div>
          </div>
          <div>
            <div className={metaLabel}>Created by</div>
            <div className="text-gray-200">{r.createdByName || "N/A"}</div>
          </div>
          <div>
            <div className={metaLabel}>Created</div>
            <div className="text-gray-200">
              {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : "N/A"}
            </div>
          </div>
        </div>

        {r.message && (
          <div className="mb-6 rounded-md bg-gray-600 p-4 border border-gray-500">
            <div className={`${metaLabel} mb-1`}>Cover note</div>
            <p className="text-sm text-gray-200 whitespace-pre-wrap">
              {r.message}
            </p>
          </div>
        )}

        <h3 className="text-sm font-semibold text-gray-300 mb-2">
          Questions ({(r.items || []).length})
        </h3>
        <div className="space-y-3 mb-6">
          {(r.items || []).map((it, i) => (
            <div
              key={it.id || i}
              className="p-4 border border-gray-600 rounded-md bg-gray-600"
            >
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h4 className="text-sm font-medium text-gray-200">
                  Question {i + 1}
                </h4>
                {it.source?.label && (
                  <span
                    className={`rounded px-1.5 py-0.5 text-[11px] ${
                      it.source.kind === "spec"
                        ? "bg-violet-500/15 text-violet-300"
                        : it.source.kind === "drawing"
                          ? "bg-sky-500/15 text-sky-300"
                          : "bg-white/5 text-gray-400"
                    }`}
                  >
                    {it.source.kind === "spec"
                      ? "§ "
                      : it.source.kind === "drawing"
                        ? "▦ "
                        : ""}
                    {it.source.label}
                  </span>
                )}
                {(r.pins || [])
                  .filter((p) => p.itemId === it.id)
                  .map((p) => (
                    <span
                      key={p.id}
                      className="rounded bg-orange-500/15 px-1.5 py-0.5 text-[11px] text-orange-200"
                    >
                      📍 page {p.page}
                    </span>
                  ))}
              </div>
              <p className="text-sm text-gray-200 whitespace-pre-wrap">
                {it.question}
              </p>
              {it.assumption && (
                <p className="mt-2 text-xs text-amber-200 whitespace-pre-wrap">
                  <span className="font-semibold">Our assumption:</span>{" "}
                  {it.assumption}
                </p>
              )}
              {it.images?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {it.images.map((im, k) => (
                    <div
                      key={im.id}
                      className="w-40 overflow-hidden rounded-md border border-gray-500 bg-black/30"
                    >
                      <RfiImage
                        file={im.file}
                        onClick={() => setBigImage(im)}
                        className="h-28 w-full cursor-zoom-in object-cover"
                      />
                      <div
                        className="truncate px-1.5 py-1 text-[10.5px] text-gray-400"
                        title={im.caption}
                      >
                        Fig. {i + 1}.{k + 1}
                        {im.caption
                          ? ` — ${im.caption}`
                          : im.source === "drawing" && im.page
                            ? ` · page ${im.page}`
                            : ""}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {it.answered && (
                <div className="mt-3 rounded-md bg-gray-800/60 p-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-green-400">
                    Answer
                    {it.answeredAt
                      ? ` · ${new Date(it.answeredAt).toLocaleDateString()}`
                      : ""}
                  </div>
                  <p className="text-sm text-green-100 whitespace-pre-wrap">
                    {it.response}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {r.log?.length > 0 && (
          <div className="rounded-md bg-gray-600 p-4 border border-gray-500 mb-6">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
              History
            </h3>
            <div className="space-y-1.5">
              {[...r.log].reverse().map((l, i) => (
                <div key={i} className="flex gap-3 text-xs">
                  <span className="w-32 shrink-0 text-gray-400">
                    {new Date(l.at).toLocaleString([], {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </span>
                  <span
                    className={`w-16 shrink-0 text-[10px] font-semibold uppercase ${
                      l.kind === "response"
                        ? "text-green-400"
                        : l.kind === "sent"
                          ? "text-sky-300"
                          : "text-gray-400"
                    }`}
                  >
                    {l.kind}
                  </span>
                  <span className="min-w-0 flex-1 whitespace-pre-wrap text-gray-200">
                    {l.text}
                    {l.by ? (
                      <span className="text-gray-400"> — {l.by}</span>
                    ) : null}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-xs text-gray-400">
          RFIs are created, edited, sent and answered in the takeoff viewer.
        </p>

        {bigImage && (
          <div
            className="fixed inset-0 z-[50000] flex flex-col items-center justify-center bg-black/85 p-6"
            onClick={() => setBigImage(null)}
          >
            <RfiImage
              file={bigImage.file}
              className="max-h-[85vh] max-w-full rounded bg-white shadow-2xl"
            />
            <div className="mt-2 text-xs text-gray-300">
              {bigImage.caption || ""}
              {bigImage.by ? ` · added by ${bigImage.by}` : ""}
              {bigImage.at
                ? ` · ${new Date(bigImage.at).toLocaleDateString()}`
                : ""}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ───────────────────────── List ─────────────────────────
  return (
    <div className="bg-gray-800 p-6 rounded-lg max-w-full mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-white">
          Requests for Information
        </h2>
      </div>

      <div className="mb-6">
        <div className="border-b border-gray-600">
          <nav className="-mb-px flex space-x-4">
            {[
              ["open", "Open"],
              ["answered", "Answered / Closed"],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`${
                  activeTab === key
                    ? "border-orange-400 text-white bg-gray-600"
                    : "border-transparent text-gray-400 hover:text-gray-200 hover:bg-gray-600"
                } whitespace-nowrap py-2 px-3 border-b-2 font-medium text-sm rounded-t-md transition-colors duration-200`}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-gray-400 text-center">
          No RFIs available for this status.
        </p>
      ) : (
        <div className="bg-gray-800 shadow-xl rounded-xl mt-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
            <thead className="bg-gray-800 sticky top-0 z-10">
              <tr>
                <th className={`${th} rounded-tl-lg`}>RFI #</th>
                <th className={th}>Title</th>
                <th className={th}>Questions</th>
                <th className={th}>Status</th>
                <th className={th}>To</th>
                <th className={th}>Due</th>
                <th className={th}>Created By</th>
                <th className={th}>Created</th>
                <th className={th}>Pictures</th>
                <th className={`${th} rounded-tr-lg`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-600">
              {filtered.map((rfi) => {
                const answered = (rfi.items || []).filter(
                  (i) => i.answered,
                ).length;
                return (
                  <tr
                    key={rfi._id}
                    onClick={() => setSelectedId(rfi._id)}
                    className="hover:bg-gray-500 cursor-pointer transition-colors duration-200"
                  >
                    <td className="px-6 py-4 text-sm text-gray-200 whitespace-nowrap">
                      {rfiNo(rfi)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-200">
                      {rfi.title || "Untitled"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-200 whitespace-nowrap">
                      {rfi.items?.length || 0}
                      {rfi.items?.length ? ` (${answered} answered)` : ""}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          statusStyle[rfi.status] || statusStyle.draft
                        }`}
                      >
                        {cap(rfi.status)}
                      </span>
                      {isOverdue(rfi) && (
                        <span className="ml-1 rounded bg-red-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-red-300">
                          Overdue
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-200">
                      {[rfi.to?.name, rfi.to?.company]
                        .filter(Boolean)
                        .join(" · ") || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-200 whitespace-nowrap">
                      {rfi.dueDate
                        ? new Date(rfi.dueDate).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-200">
                      {rfi.createdByName || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-200 whitespace-nowrap">
                      {rfi.createdAt
                        ? new Date(rfi.createdAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-200 whitespace-nowrap">
                      {pictureCount(rfi) || "None"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedId(rfi._id);
                        }}
                        className="text-orange-400 hover:text-orange-300"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
